import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome, faTags, faShoppingCart, faClock, faFileAlt, faHeart, faBell, faStar, faUserFriends,
  faCog, faUser, faLock, faLifeRing, faShieldAlt, faPlus, faChartBar, faEnvelopeOpenText, faBullhorn,
  faMoneyBillWave, faUsers, faBuilding, faCheckToSlot, faStethoscope, faClipboardList
} from '@fortawesome/free-solid-svg-icons';

const SidebarMenu = ({ selectedRole, onSelectPage }) => {
  const [openMenus, setOpenMenus] = useState({});
  const toggleSubMenu = (menu) => setOpenMenus(prev => ({ ...prev, [menu]: !prev[menu] }));

  const MenuItem = ({ icon, label, page }) => (
    <li
      onClick={() => onSelectPage(page, label)}
      className="cursor-pointer p-2 hover:bg-green-700 text-white flex items-center gap-2"
    >
      <FontAwesomeIcon icon={icon} /> {label}
    </li>
  );

  const SubMenu = ({ label, icon, children }) => (
    <div>
      <div
        onClick={() => toggleSubMenu(label)}
        className="cursor-pointer p-2 hover:bg-green-800 flex items-center justify-between"
      >
        <span className="flex items-center gap-2"><FontAwesomeIcon icon={icon} /> {label}</span>
        <span>{openMenus[label] ? '▲' : '▼'}</span>
      </div>
      {openMenus[label] && (
        <ul className="ml-4 border-l border-green-400 pl-2">
          {children}
        </ul>
      )}
    </div>
  );

  const renderCustomerMenu = () => (
    <>
      <MenuItem icon={faTags} label="Browse Medicines" page="browse-medi" />
      <SubMenu label="My Purchases" icon={faShoppingCart}>
        <MenuItem icon={faShoppingCart} label="Active Medicines" page="active-purchases" />
        <MenuItem icon={faClock} label="Expired Medicines" page="expired-purchases" />
        <MenuItem icon={faFileAlt} label="Voucher History" page="voucher-history" />
      </SubMenu>
      <MenuItem icon={faHeart} label="Favorites" page="favorites" />
      <MenuItem icon={faBell} label="Notifications" page="notifications" />
      <MenuItem icon={faStar} label="Reviews" page="reviews" />
      <MenuItem icon={faUserFriends} label="Referral & Loyalty" page="referral-loyalty" />
      <SubMenu label="Settings" icon={faCog}>
        <MenuItem icon={faUser} label="Profile" page="profile" />
        <MenuItem icon={faLock} label="Account Security" page="account-security" />
        <MenuItem icon={faCog} label="Preferences" page="preferences" />
      </SubMenu>
      <MenuItem icon={faLifeRing} label="Support" page="support" />
    </>
  );

  const renderPartnerMenu = () => (
    <>
      <MenuItem icon={faShieldAlt} label="My Profile" page="business-profile" />
      <SubMenu label="Medicine Center" icon={faTags}>
        <MenuItem icon={faPlus} label="Submit New Medicine" page="submit-medi" />
        <MenuItem icon={faTags} label="Manage Medicines" page="manage-medi" />
        <MenuItem icon={faChartBar} label="Performance Analytics" page="medi-performance" />
      </SubMenu>
      <SubMenu label="Customer Engagement" icon={faEnvelopeOpenText}>
        <MenuItem icon={faEnvelopeOpenText} label="Inquiries" page="customer-inquiries" />
        <MenuItem icon={faStar} label="Respond to Reviews" page="respond-reviews" />
        <MenuItem icon={faBullhorn} label="Send Offers" page="send-offers" />
      </SubMenu>
      <SubMenu label="Analytics" icon={faChartBar}>
        <MenuItem icon={faChartBar} label="Sales Reports" page="sales-reports" />
        <MenuItem icon={faChartBar} label="Redemption Metrics" page="redemption-metrics" />
        <MenuItem icon={faUsers} label="Customer Insights" page="customer-insights" />
      </SubMenu>
      <SubMenu label="Earnings" icon={faMoneyBillWave}>
        <MenuItem icon={faMoneyBillWave} label="Payment History" page="payment-history" />
        <MenuItem icon={faCog} label="Payment Settings" page="payment-settings" />
      </SubMenu>
      <SubMenu label="Settings" icon={faCog}>
        <MenuItem icon={faBuilding} label="Business Info" page="business-info" />
        <MenuItem icon={faUsers} label="Team Access" page="team-access" />
        <MenuItem icon={faLock} label="Security" page="business-security" />
      </SubMenu>
      <MenuItem icon={faLifeRing} label="Support" page="business-support" />
    </>
  );

  const renderAdminMenu = () => (
    <>
      <SubMenu label="Manage Users" icon={faUsers}>
        <MenuItem icon={faUsers} label="View Consumers" page="admin-users" />
        <MenuItem icon={faUserFriends} label="Flags & Issues" page="user-flags" />
      </SubMenu>
      <SubMenu label="Manage Businesses" icon={faBuilding}>
        <MenuItem icon={faBuilding} label="Pending Vendors" page="pending-vendors" />
        <MenuItem icon={faBuilding} label="Approved Vendors" page="approved-vendors" />
        <MenuItem icon={faChartBar} label="Performance" page="vendor-performance" />
      </SubMenu>
      <SubMenu label="Medicine Moderation" icon={faTags}>
        <MenuItem icon={faCheckToSlot} label="Approve/Reject" page="moderate-medi" />
        <MenuItem icon={faFileAlt} label="Expired Logs" page="expired-logs" />
      </SubMenu>
      <SubMenu label="Marketing Tools" icon={faBullhorn}>
        <MenuItem icon={faEnvelopeOpenText} label="Email Campaigns" page="email-campaigns" />
        <MenuItem icon={faBell} label="Push Notifications" page="push-notifications" />
        <MenuItem icon={faTags} label="Featured Medicines" page="featured-medi" />
      </SubMenu>
      <SubMenu label="Analytics & Reports" icon={faChartBar}>
        <MenuItem icon={faChartBar} label="Platform Stats" page="platform-stats" />
        <MenuItem icon={faChartBar} label="User Trends" page="user-trends" />
        <MenuItem icon={faMoneyBillWave} label="Revenue Reports" page="revenue-reports" />
      </SubMenu>
      <SubMenu label="Settings" icon={faCog}>
        <MenuItem icon={faCog} label="Platform Config" page="platform-config" />
        <MenuItem icon={faLock} label="Security Logs" page="security-logs" />
        <MenuItem icon={faFileAlt} label="Data Compliance" page="data-compliance" />
      </SubMenu>
      <MenuItem icon={faLifeRing} label="Support" page="admin-support" />
    </>
  );

  const renderDoctorMenu = () => (
    <>
      <MenuItem icon={faUser} label="Doctor Profile" page="doctor-profile" />
      <SubMenu label="Patient Management" icon={faUserFriends}>
        <MenuItem icon={faClipboardList} label="View Appointments" page="view-appointments" />
        <MenuItem icon={faFileAlt} label="Patient Records" page="patient-records" />
        <MenuItem icon={faBullhorn} label="Prescriptions & Advice" page="prescriptions" />
      </SubMenu>
      <SubMenu label="Reports" icon={faChartBar}>
        <MenuItem icon={faChartBar} label="Appointment Stats" page="appointment-stats" />
        <MenuItem icon={faChartBar} label="Feedback Summary" page="feedback-summary" />
      </SubMenu>
      <SubMenu label="Settings" icon={faCog}>
        <MenuItem icon={faUser} label="Profile Settings" page="doc-profile-settings" />
        <MenuItem icon={faLock} label="Change Password" page="doc-change-password" />
      </SubMenu>
      <MenuItem icon={faLifeRing} label="Support" page="doctor-support" />
    </>
  );

  const renderMenuByRole = () => {
    switch (selectedRole) {
      case 'Customer': return renderCustomerMenu();
      case 'Partner': return renderPartnerMenu();
      case 'Admin': return renderAdminMenu();
      case 'Doctor': return renderDoctorMenu();
      default: return null;
    }
  };

  return (
    <div className="flex flex-col w-full sm:w-2/5 md:w-1/4 lg:w-1/5 bg-green-600 text-white border-2 border-green-400 shadow-md h-screen overflow-y-auto">
      <ul className="p-2">
        <MenuItem icon={faHome} label={`${selectedRole} Dashboard`} page="dashboard" />
        {renderMenuByRole()}
      </ul>
    </div>
  );
};

export default SidebarMenu;
